import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowRight, Wallet, Briefcase, Clock, Target, FileText } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const STEPS = [
  { id: 1, title: "Capacité", icon: Wallet },
  { id: 2, title: "Expérience", icon: Briefcase },
  { id: 3, title: "Projet", icon: Clock },
  { id: 4, title: "Critères", icon: Target },
  { id: 5, title: "Contact", icon: FileText },
];

const CAPACITE_OPTIONS = [
  { value: "moins_100k", label: "Moins de 100 000 €", points: 5 },
  { value: "100k_200k", label: "100 000 € - 200 000 €", points: 10 },
  { value: "200k_500k", label: "200 000 € - 500 000 €", points: 20 },
  { value: "500k_1m", label: "500 000 € - 1 M€", points: 30 },
  { value: "plus_1m", label: "Plus de 1 M€", points: 30 },
];

const EXPERIENCE_OPTIONS = [
  { value: "salarie", label: "Salarié sans expérience de management", points: 5 },
  { value: "manager", label: "Manager / Chef d'équipe", points: 10 },
  { value: "cadre_dirigeant", label: "Cadre dirigeant", points: 20 },
  { value: "dirigeant_actuel", label: "Dirigeant actuel", points: 25 },
  { value: "ex_dirigeant", label: "Ex-dirigeant / Entrepreneur", points: 25 },
];

const HORIZON_OPTIONS = [
  { value: "moins_3mois", label: "Dans les 3 prochains mois", points: 25 },
  { value: "3_6mois", label: "Dans 3 à 6 mois", points: 15 },
  { value: "6_12mois", label: "Dans 6 à 12 mois", points: 10 },
  { value: "plus_12mois", label: "Dans plus de 12 mois (veille)", points: 5 },
];

const SECTEURS = [
  { value: "industrie", label: "Industrie / Production" },
  { value: "services", label: "Services aux entreprises" },
  { value: "commerce", label: "Commerce / Distribution" },
  { value: "artisanat", label: "Artisanat" },
  { value: "tech", label: "Tech / Digital" },
  { value: "sante", label: "Santé / Médical" },
  { value: "batiment", label: "BTP / Bâtiment" },
  { value: "restauration", label: "Restauration / Hôtellerie" },
  { value: "tous", label: "Tous secteurs" },
];

const ZONES = [
  { value: "aura", label: "Auvergne-Rhône-Alpes" },
  { value: "paca", label: "Provence-Alpes-Côte d'Azur" },
  { value: "occitanie", label: "Occitanie" },
  { value: "national", label: "France entière" },
];

const ACCORD_OPTIONS = [
  { value: "oui", label: "Oui, j'ai un pré-accord", points: 10 },
  { value: "en_cours", label: "Discussions en cours", points: 5 },
  { value: "non", label: "Pas encore contacté", points: 0 },
];

const BUDGET_OPTIONS = [
  { value: "moins_200k", label: "Moins de 200 000 €" },
  { value: "200k_500k", label: "200 000 € - 500 000 €" },
  { value: "500k_1m", label: "500 000 € - 1 M€" },
  { value: "1m_3m", label: "1 M€ - 3 M€" },
  { value: "plus_3m", label: "Plus de 3 M€" },
];

const DISPONIBILITE_OPTIONS = [
  { value: "temps_plein", label: "Temps plein immédiat" },
  { value: "transition", label: "Période de transition nécessaire" },
  { value: "temps_partiel", label: "Temps partiel au début" },
];

const ACCOMPAGNEMENT_OPTIONS = [
  { value: "autonome", label: "Autonome - Peu d'accompagnement" },
  { value: "ponctuel", label: "Accompagnement ponctuel" },
  { value: "complet", label: "Accompagnement complet souhaité" },
];

export default function RepreneurQualification() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1 - Capacité
    capacite_financiere: "",
    // Step 2 - Expérience
    experience: "",
    // Step 3 - Projet
    horizon_projet: "",
    pre_accord_bancaire: "",
    disponibilite: "",
    // Step 4 - Critères
    secteurs_cibles: [],
    zones_geographiques: [],
    budget_max: "",
    accompagnement_souhaite: "",
    motivation: "",
    // Step 5 - Contact
    email: "",
    prenom: "",
    nom: "",
    telephone: "",
    secteur_activite: "",
  });

  const progress = (currentStep / STEPS.length) * 100;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.capacite_financiere !== "";
      case 2:
        return formData.experience !== "";
      case 3:
        return formData.horizon_projet !== "" && formData.pre_accord_bancaire !== "";
      case 4:
        return formData.secteurs_cibles.length > 0 && formData.zones_geographiques.length > 0;
      case 5:
        return formData.email !== "" && formData.prenom !== "" && formData.nom !== "";
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // const handleSubmit = async () => {
  //   setIsSubmitting(true);
  //   try {
  //     const payload = {
  //       email: formData.email,
  //       prenom: formData.prenom,
  //       nom: formData.nom,
  //       telephone: formData.telephone || null,
  //       qualification: {
  //         capacite_financiere: formData.capacite_financiere,
  //         experience: formData.experience,
  //         horizon_projet: formData.horizon_projet,
  //         secteurs_cibles: formData.secteurs_cibles,
  //         zones_geographiques: formData.zones_geographiques,
  //         pre_accord_bancaire: formData.pre_accord_bancaire,
  //         motivation: formData.motivation || "",
  //         disponibilite: formData.disponibilite || "",
  //         accompagnement_souhaite: formData.accompagnement_souhaite || "",
  //         budget_max: formData.budget_max || "",
  //       },
  //     };

  //     const response = await axios.post(`${API}/repreneurs`, payload);
  //     toast.success("Profil créé avec succès !");
      
  //     localStorage.setItem("repreneur_result", JSON.stringify(response.data));
  //     navigate("/repreneur/resultat");
  //   } catch (error) {
  //     console.error(error);
  //     toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  const handleSubmit = async () => {
  setIsSubmitting(true);
  try {
    const payload = {
      email: formData.email,
      prenom: formData.prenom,
      nom: formData.nom,
      telephone: formData.telephone || null,
      qualification: {
        capacite_financiere: formData.capacite_financiere,
        experience: formData.experience,
        horizon_projet: formData.horizon_projet,
        secteurs_cibles: formData.secteurs_cibles,
        zones_geographiques: formData.zones_geographiques,
        pre_accord_bancaire: formData.pre_accord_bancaire,
        motivation: formData.motivation || "",
        disponibilite: formData.disponibilite || "",
        accompagnement_souhaite: formData.accompagnement_souhaite || "",
        budget_max: formData.budget_max || "",
      },
      secteur_activite: formData.secteur_activite || null,
    };

    // Stocker temporairement les données
    sessionStorage.setItem("temp_user_data", JSON.stringify(payload));
    
    // Naviguer vers SignUp avec le type d'utilisateur
    navigate("/final-signup", { 
      state: { 
        formData: payload,
        userType: "repreneur"
      } 
    });
    
  } catch (error) {
    console.error(error);
    toast.error("Erreur lors de la préparation de l'inscription");
  } finally {
    setIsSubmitting(false);
  }
};
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Quelle est votre capacité d'apport personnel ? *
              </h2>
              <p className="text-lg text-foreground/60">
                L'apport détermine le type d'entreprises accessibles
              </p>
            </div>
            <div className="space-y-4 max-w-lg mx-auto">
              {CAPACITE_OPTIONS.map((option) => (
                <Card
                  key={option.value}
                  className={`p-6 cursor-pointer transition-all duration-300 rounded-lg border-2 flex items-center justify-between ${
                    formData.capacite_financiere === option.value
                      ? "border-accent bg-accent/5 shadow-md"
                      : "border-transparent bg-muted hover:bg-muted/80"
                  }`}
                  onClick={() => handleChange("capacite_financiere", option.value)}
                  data-testid={`capacite-${option.value}`}
                >
                  <span className="text-lg font-semibold text-primary">{option.label}</span>
                </Card>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Quelle est votre expérience professionnelle ? *
              </h2>
              <p className="text-lg text-foreground/60">
                Votre parcours influence votre capacité à reprendre et gérer une entreprise
              </p>
            </div>
            <div className="space-y-4 max-w-lg mx-auto">
              {EXPERIENCE_OPTIONS.map((option) => (
                <Card
                  key={option.value}
                  className={`p-6 cursor-pointer transition-all duration-300 rounded-lg border-2 flex items-center justify-between ${
                    formData.experience === option.value
                      ? "border-accent bg-accent/5 shadow-md"
                      : "border-transparent bg-muted hover:bg-muted/80"
                  }`}
                  onClick={() => handleChange("experience", option.value)}
                  data-testid={`experience-${option.value}`}
                >
                  <span className="text-lg font-semibold text-primary">{option.label}</span>
                </Card>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Où en êtes-vous dans votre projet ?
              </h2>
              <p className="text-lg text-foreground/60">
                Votre niveau de préparation détermine votre priorité
              </p>
            </div>
            <div className="space-y-10 max-w-xl mx-auto">
              <div className="space-y-4">
                <Label className="text-xl font-bold text-primary">Horizon de reprise *</Label>
                <div className="space-y-3">
                    {HORIZON_OPTIONS.map((option) => (
                    <Card
                        key={option.value}
                        className={`p-5 cursor-pointer transition-all duration-300 rounded-lg border-2 flex items-center justify-between ${
                        formData.horizon_projet === option.value
                            ? "border-accent bg-accent/5 shadow-md"
                            : "border-transparent bg-muted hover:bg-muted/80"
                        }`}
                        onClick={() => handleChange("horizon_projet", option.value)}
                        data-testid={`horizon-${option.value}`}
                    >
                        <span className="text-lg font-semibold text-primary">{option.label}</span>
                    </Card>
                    ))}
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-xl font-bold text-primary">Pré-accord bancaire *</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {ACCORD_OPTIONS.map((option) => (
                    <Card
                        key={option.value}
                        className={`p-5 cursor-pointer transition-all duration-300 rounded-lg border-2 flex flex-col items-center gap-3 ${
                        formData.pre_accord_bancaire === option.value
                            ? "border-accent bg-accent/5 shadow-md"
                            : "border-transparent bg-muted hover:bg-muted/80"
                        }`}
                        onClick={() => handleChange("pre_accord_bancaire", option.value)}
                        data-testid={`accord-${option.value}`}
                    >
                        <span className="text-base font-bold text-primary text-center">{option.label}</span>
                    </Card>
                    ))}
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-xl font-bold text-primary">Disponiblité à transition ( Optionnel )</Label>
                <Select
                  value={formData.disponibilite}
                  onValueChange={(value) => handleChange("disponibilite", value)}
                >
                  <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-disponibilite">
                    <SelectValue placeholder="Votre disponibilité" />
                  </SelectTrigger>
                  <SelectContent>
                    {DISPONIBILITE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Quels sont vos critères de recherche ?
              </h2>
              <p className="text-lg text-foreground/60">
                Plus vos critères sont précis, meilleur sera le matching
              </p>
            </div>
            <div className="space-y-10 max-w-3xl mx-auto">
              <div className="space-y-4">
                <Label className="text-xl font-bold text-primary">Secteurs d'activité ciblés *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {SECTEURS.map((secteur) => (
                    <div
                      key={secteur.value}
                      className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.secteurs_cibles.includes(secteur.value)
                          ? "border-accent bg-accent/5 shadow-sm"
                          : "border-transparent bg-muted hover:bg-muted/80"
                      }`}
                      onClick={() => handleArrayToggle("secteurs_cibles", secteur.value)}
                      data-testid={`secteur-${secteur.value}`}
                    >
                      <Checkbox
                        checked={formData.secteurs_cibles.includes(secteur.value)}
                        className="pointer-events-none data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                      />
                      <span className="text-base font-semibold text-primary">{secteur.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-xl font-bold text-primary">Zones géographiques *</Label>
                <div className="grid grid-cols-2 gap-4">
                  {ZONES.map((zone) => (
                    <div
                      key={zone.value}
                      className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.zones_geographiques.includes(zone.value)
                          ? "border-accent bg-accent/5 shadow-sm"
                          : "border-transparent bg-muted hover:bg-muted/80"
                      }`}
                      onClick={() => handleArrayToggle("zones_geographiques", zone.value)}
                      data-testid={`zone-${zone.value}`}
                    >
                      <Checkbox
                        checked={formData.zones_geographiques.includes(zone.value)}
                        className="pointer-events-none data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                      />
                      <span className="text-base font-semibold text-primary">{zone.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-4">
                  <Label className="text-xl font-bold text-primary">Budget maximum</Label>
                  <Select
                    value={formData.budget_max}
                    onValueChange={(value) => handleChange("budget_max", value)}
                  >
                    <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-budget">
                      <SelectValue placeholder="Budget max" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-4">
                  <Label className="text-xl font-bold text-primary">Accompagnement souhaité</Label>
                  <Select
                    value={formData.accompagnement_souhaite}
                    onValueChange={(value) => handleChange("accompagnement_souhaite", value)}
                  >
                    <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-accompagnement">
                      <SelectValue placeholder="Niveau" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOMPAGNEMENT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-xl font-bold text-primary">Motivation (optionnel)</Label>
                <Textarea
                  className="text-lg rounded-lg border-border/50 p-4"
                  value={formData.motivation}
                  onChange={(e) => handleChange("motivation", e.target.value)}
                  placeholder="Qu'est-ce qui vous motive à reprendre une entreprise ?"
                  rows={4}
                  data-testid="textarea-motivation"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-primary mb-3">
                Vos coordonnées
              </h2>
              <p className="text-lg text-foreground/60">
                Pour vous contacter et vous présenter les opportunités adaptées
              </p>
            </div>
            <div className="space-y-6 max-w-xl mx-auto">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="prenom" className="text-lg font-bold text-primary">Prénom *</Label>
                  <Input
                    id="prenom"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={formData.prenom}
                    onChange={(e) => handleChange("prenom", e.target.value)}
                    placeholder="Votre prénom"
                    data-testid="input-prenom"
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="nom" className="text-lg font-bold text-primary">Nom *</Label>
                  <Input
                    id="nom"
                    className="h-14 text-lg rounded-lg border-border/50"
                    value={formData.nom}
                    onChange={(e) => handleChange("nom", e.target.value)}
                    placeholder="Votre nom"
                    data-testid="input-nom"
                  />
                </div>
              </div>
              <div className="space-y-3">
                <Label htmlFor="email" className="text-lg font-bold text-primary">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  className="h-14 text-lg rounded-lg border-border/50"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="votre@email.com"
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="telephone" className="text-lg font-bold text-primary">Téléphone</Label>
                <Input
                  id="telephone"
                  className="h-14 text-lg rounded-lg border-border/50"
                  value={formData.telephone}
                  onChange={(e) => handleChange("telephone", e.target.value)}
                  placeholder="06 XX XX XX XX"
                  data-testid="input-telephone"
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="secteur_activite" className="text-lg font-bold text-primary">Secteur d'activité actuel</Label>
                <Select
                  value={formData.secteur_activite}
                  onValueChange={(value) => handleChange("secteur_activite", value)}
                >
                  <SelectTrigger className="h-14 text-lg rounded-lg border-border/50" data-testid="select-secteur-activite">
                    <SelectValue placeholder="Votre secteur actuel" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.secteurs_cibles.map((slug) => {
                      const secteur = SECTEURS.find(s => s.value === slug);
                      return (
                        <SelectItem key={slug} value={slug}>
                          {secteur ? secteur.label : slug}
                        </SelectItem>
                      );
                    })}
                    <SelectItem value="autres">Autres</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background py-16 px-8" data-testid="repreneur-qualification">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-16">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-foreground/60 hover:text-primary transition-colors font-medium"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
          <span className="text-3xl font-bold text-primary">Sequenti</span>
        </div>

        {/* Progress */}
        <div className="mb-16">
          <div className="flex justify-between mb-6">
            {STEPS.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex flex-col items-center transition-opacity duration-300 ${
                    step.id <= currentStep ? "opacity-100" : "opacity-40"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                      step.id <= currentStep ? "bg-primary text-white scale-110 shadow-md" : "bg-muted text-primary"
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className={`text-sm font-bold uppercase tracking-wider hidden md:block ${
                      step.id <= currentStep ? "text-primary" : "text-foreground/40"
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-2 bg-muted transition-all duration-500" />
        </div>

        {/* Step Content */}
        <Card className="p-12 mb-12 border-none bg-white rounded-lg shadow-sm border border-border/40">
          {renderStep()}
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="text-lg px-8 h-14 hover:bg-muted"
            data-testid="prev-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Précédent
          </Button>
          {currentStep < STEPS.length ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-primary text-white hover:bg-primary/90 text-lg px-12 h-14 rounded-lg shadow-md transition-all active:scale-95"
              data-testid="next-button"
            >
              Suivant
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className="bg-accent text-white hover:bg-accent/90 text-lg px-12 h-14 rounded-lg shadow-md transition-all active:scale-95"
              data-testid="submit-button"
            >
              {isSubmitting ? "Envoi en cours..." : "Évaluer mon profil"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
